import React, { useState, useEffect } from "react";
import {
  Users,
  Mail,
  Calendar,
  Clock,
  Copy,
  Download,
  Eye,
  EyeOff,
  AlertCircle,
  CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { formatDateToBritish } from "@/lib/dateUtils";
import { OperationsDashboardService } from "@/services/operationsDashboardService";
import { DemoAccount, DemoAccountsSummary, DemoEmailList } from "@/types/demo";

const DemoAccountsList: React.FC = () => {
  const [demoData, setDemoData] = useState<DemoAccountsSummary | null>(null);
  const [emailData, setEmailData] = useState<DemoEmailList | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [includePurged, setIncludePurged] = useState<boolean>(true);
  const [emailsOnly, setEmailsOnly] = useState<boolean>(false);
  const [copySuccess, setCopySuccess] = useState<string | null>(null);

  // Fetch demo accounts data
  const fetchDemoAccounts = async () => {
    try {
      setIsLoading(true);
      setError(null);
      
      if (emailsOnly) {
        const data = await OperationsDashboardService.getDemoAccounts(includePurged, true) as DemoEmailList;
        setEmailData(data);
        setDemoData(null);
      } else {
        const data = await OperationsDashboardService.getDemoAccounts(includePurged, false) as DemoAccountsSummary;
        setDemoData(data);
        setEmailData(null);
      }
    } catch (err) {
      setError("Failed to load demo accounts. Please try again later.");
      console.error(err);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch data on component mount and when filters change
  useEffect(() => {
    fetchDemoAccounts();
  }, [includePurged, emailsOnly]);

  // Copy emails to clipboard
  const copyEmailsToClipboard = async () => {
    try {
      const emails = emailsOnly && emailData ? emailData.emails : demoData?.filtered_emails || [];
      const emailText = emails.join('\n');
      await navigator.clipboard.writeText(emailText);
      setCopySuccess('Emails copied to clipboard!');
      setTimeout(() => setCopySuccess(null), 3000);
    } catch (err) {
      setCopySuccess('Failed to copy emails');
      setTimeout(() => setCopySuccess(null), 3000);
    }
  };

  // Export emails as CSV
  const exportEmailsAsCSV = () => {
    const emails = emailsOnly && emailData ? emailData.emails : demoData?.filtered_emails || [];
    const csvContent = "data:text/csv;charset=utf-8," + 
      "Email\n" + 
      emails.map(email => `"${email}"`).join('\n');
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `demo-accounts-emails-${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Format date for display (UK format DD/MM/YYYY)
  const formatDate = (dateString: string | undefined | null): string => {
    if (!dateString) return "Never";
    try {
      return formatDateToBritish(dateString);
    } catch {
      return "Invalid date";
    }
  };

  // Render loading state
  if (isLoading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Demo Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading demo accounts...</p>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Render error state
  if (error) {
    return (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Demo Accounts
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-center py-8">
            <div className="text-center">
              <AlertCircle className="h-8 w-8 text-destructive mx-auto mb-4" />
              <p className="text-destructive font-medium">Error Loading Demo Accounts</p>
              <p className="text-muted-foreground mt-2">{error}</p>
              <Button onClick={fetchDemoAccounts} className="mt-4">
                Try Again
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Get summary stats
  const totalEmails = emailsOnly && emailData ? emailData.total_count : demoData?.filtered_emails.length || 0;
  const activeCount = emailsOnly && emailData ? emailData.active_count : demoData?.total_active || 0;
  const purgedCount = emailsOnly && emailData ? emailData.purged_count : demoData?.total_purged || 0;
  const excludedDomains = emailsOnly && emailData ? emailData.excluded_domains : ['nannypaypro.com', 'hirelo.io'];

  return (
    <div className="space-y-6">
      {/* Controls */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Users className="h-5 w-5" />
            Demo Accounts
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filter Controls */}
          <div className="flex flex-wrap items-center gap-6">
            <div className="flex items-center space-x-2">
              <Switch
                id="include-purged"
                checked={includePurged}
                onCheckedChange={setIncludePurged}
              />
              <Label htmlFor="include-purged">Include purged accounts</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Switch
                id="emails-only"
                checked={emailsOnly}
                onCheckedChange={setEmailsOnly}
              />
              <Label htmlFor="emails-only">Emails only view</Label>
            </div>
          </div>

          <Separator />

          {/* Summary Stats */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="flex items-center gap-2">
              <Mail className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-sm font-medium">Total Filtered Emails</p>
                <p className="text-2xl font-bold">{totalEmails}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-green-500" />
              <div>
                <p className="text-sm font-medium">Active Accounts</p>
                <p className="text-2xl font-bold">{activeCount}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 text-orange-500" />
              <div>
                <p className="text-sm font-medium">Purged Accounts</p>
                <p className="text-2xl font-bold">{purgedCount}</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <EyeOff className="h-4 w-4 text-gray-500" />
              <div>
                <p className="text-sm font-medium">Excluded Domains</p>
                <p className="text-sm text-muted-foreground">
                  {excludedDomains.join(', ')}
                </p>
              </div>
            </div>
          </div>

          <Separator />

          {/* Action Buttons */}
          <div className="flex flex-wrap items-center gap-3">
            <Button onClick={copyEmailsToClipboard} variant="outline" size="sm">
              <Copy className="h-4 w-4 mr-2" />
              Copy Emails
            </Button>
            <Button onClick={exportEmailsAsCSV} variant="outline" size="sm">
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
            <Button onClick={fetchDemoAccounts} variant="outline" size="sm">
              Refresh
            </Button>
            {copySuccess && (
              <Badge variant="secondary" className="ml-2">
                {copySuccess}
              </Badge>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Emails Only View */}
      {emailsOnly && emailData && (
        <Card>
          <CardHeader>
            <CardTitle>Filtered Demo Account Emails</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="max-h-96 overflow-y-auto">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                {emailData.emails.map((email, index) => (
                  <div key={index} className="p-2 bg-muted rounded text-sm font-mono">
                    {email}
                  </div>
                ))}
              </div>
              {emailData.emails.length === 0 && (
                <p className="text-center text-muted-foreground py-8">
                  No demo account emails found with current filters.
                </p>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Full Account Details View */}
      {!emailsOnly && demoData && (
        <>
          {/* Active Accounts */}
          {demoData.active_accounts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CheckCircle2 className="h-5 w-5 text-green-500" />
                  Active Demo Accounts ({demoData.total_active})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Email</TableHead>
                      <TableHead>Customer Email</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Last Login</TableHead>
                      <TableHead>Expires</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {demoData.active_accounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell className="font-mono text-sm">
                          {account.email}
                        </TableCell>
                        <TableCell className="font-mono text-sm">
                          {account.demo_customer_email || "N/A"}
                        </TableCell>
                        <TableCell>{formatDate(account.date_joined)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3" />
                            {formatDate(account.last_login)}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={
                            account.demo_expiry_date && new Date(account.demo_expiry_date) < new Date() 
                              ? "destructive" 
                              : "secondary"
                          }>
                            {formatDate(account.demo_expiry_date)}
                          </Badge>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* Purged Accounts */}
          {includePurged && demoData.purged_accounts.length > 0 && (
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <AlertCircle className="h-5 w-5 text-orange-500" />
                  Purged Demo Accounts ({demoData.total_purged})
                </CardTitle>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Original Email</TableHead>
                      <TableHead>Purged Email</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Last Login</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {demoData.purged_accounts.map((account) => (
                      <TableRow key={account.id}>
                        <TableCell className="font-mono text-sm">
                          {account.original_email || "Unknown"}
                        </TableCell>
                        <TableCell className="font-mono text-xs text-muted-foreground">
                          {account.email}
                        </TableCell>
                        <TableCell>{formatDate(account.date_joined)}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Clock className="h-3 w-3" />
                            {formatDate(account.last_login)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}

          {/* No Data State */}
          {demoData.active_accounts.length === 0 && demoData.purged_accounts.length === 0 && (
            <Card>
              <CardContent className="py-8">
                <div className="text-center">
                  <Users className="h-8 w-8 text-muted-foreground mx-auto mb-4" />
                  <p className="text-muted-foreground">No demo accounts found with current filters.</p>
                </div>
              </CardContent>
            </Card>
          )}
        </>
      )}
    </div>
  );
};

export default DemoAccountsList;
